"use client";
import React, { useEffect, useRef } from "react";
import Image from "next/image";
import PropTypes from "prop-types";
import cn from "classnames";
import { createPortal } from "react-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import {
  useBrochureState,
  useBrochureUpdate,
} from "@/next-src/context/Brochure";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import Button from "@/next-src/components/ui/button/Button";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";

import comCSS from "./BrochureModal.module.scss";

const Brochure = ({ className = "", data }) => {
  const { isShown } = useBrochureState();
  const { setIsShown } = useBrochureUpdate();
  const { width: windowWidth } = useWindowDimensions();

  const {
    titleMobile,
    textMobile,
    form,
    form: { buttonMobile, buttonSubmitingMobile, brochureLink },
    file,
    image,
  } = data || {};

  let {
    title,
    text,
    form: { button, buttonSubmiting },
  } = data || {};

  if (windowWidth < 992) {
    title = titleMobile;
    text = textMobile;
    button = buttonMobile;
    buttonSubmiting = buttonSubmitingMobile;
  }

  // Define validation schema using Yup
  const validationSchema = Yup.object().shape({
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
    phoneNumber: Yup.string().required("Phone number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
  });

  // Initial form values
  const initialValues = {
    firstname: "",
    lastname: "",
    phoneNumber: "",
    email: "",
  };

  const onSubmit = async (values, actions) => {
    const reqData = {
      ...values,
      brochureLink: brochureLink || "",
      url: window.location?.toString() || "",
    };
    try {
      const res = await axios({
        method: "POST",
        data: reqData,
        url: "/api/brochure-downloads",
      });
    } catch (error) {
      console.log("error", error);
    }
    window.location = brochureLink;
    // actions.setSubmitting(false); // Important: This line tells Formik that submission is complete
  };

  return (
    <div
      className={cn(className, comCSS.wrapper, "download-brochure-pop-up", {
        [comCSS.active]: isShown,
      })}
    >
      <div
        className={cn("brochure-modal-wrapper", comCSS.modalWrapper, {
          [comCSS.active]: isShown,
        })}
      >
        <section className="section">
          <div className="padding-global">
            <div className="container-large">
              <div className="custom-to-nav-section-padding">
                <div className="download-brochure-overflow-auto-wrap">
                  <div className="brochure-download-card">
                    <div className="download-brochure-form-wrap">
                      <div className="brochure-form-heading-wrap">
                        <div className="margin-bottom margin-xsmall">
                          {convertHTMLtoJSX(title)}
                        </div>
                        <div className="max-width-small">
                          {convertHTMLtoJSX(text)}
                        </div>
                      </div>
                      <div
                        id="download-brochure-form"
                        className="cta-form-block no-max-width w-form"
                      >
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={onSubmit}
                        >
                          {({ isSubmitting }) => (
                            <Form
                              id="wf-form-Download-Brochure-Form"
                              name="wf-form-Download-Brochure-Form"
                              data-name="Download Brochure Form"
                              className="cta_form"
                              data-wf-page-id="63d939480c18d8d2008bd97b"
                              data-wf-element-id="6314b862-c672-3e38-68ee-a5f17085997d"
                              aria-label="Download Brochure Form"
                            >
                              <div className="margin-top margin-medium">
                                <div className="form-input-double-wrap no-gap">
                                  <div>
                                    <Field
                                      className="form-input is-custom no-left-padding-desktop w-input"
                                      maxLength="256"
                                      name="firstname"
                                      placeholder={form?.firstnamePlaceholder}
                                      type="text"
                                      id="first-name-4"
                                    />
                                    <div className="form-row-divider" />
                                    <ErrorMessage
                                      name="firstname"
                                      component="div"
                                      className={comCSS.error}
                                    />
                                  </div>
                                  <div>
                                    <Field
                                      className="form-input is-custom no-left-padding-desktop w-input"
                                      maxLength="256"
                                      name="lastname"
                                      placeholder={form?.lastnamePlaceholder}
                                      type="text"
                                      id="last-name-3"
                                    />
                                    <div className="form-row-divider" />
                                    <ErrorMessage
                                      name="lastname"
                                      component="div"
                                      className={comCSS.error}
                                    />
                                  </div>
                                </div>
                                <Field
                                  className="form-input is-custom no-left-padding-desktop w-input"
                                  maxLength="256"
                                  name="phoneNumber"
                                  placeholder={form?.phoneNumberPlaceholder}
                                  type="tel"
                                  id="phone-number-2"
                                />
                                <div className="form-row-divider" />
                                <ErrorMessage
                                  name="phoneNumber"
                                  component="div"
                                  className={comCSS.error}
                                />
                                <Field
                                  className="form-input is-custom no-left-padding-desktop w-input"
                                  maxLength="256"
                                  name="email"
                                  placeholder={form?.emailPlaceholder}
                                  type="email"
                                  id="email-6"
                                />
                                <div className="form-row-divider" />
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className={comCSS.error}
                                />
                                <div className="contact-form-line-wrap distribute no-left-padding-desktop">
                                  <div
                                    className={cn(
                                      "text-color-slate-grey text-size-tiny",
                                      comCSS.privacyPolicy
                                    )}
                                  >
                                    {convertHTMLtoJSX(form?.privacyPolicyText)}
                                  </div>
                                  {isSubmitting ? (
                                    <Button
                                      type="submit"
                                      data={buttonSubmiting}
                                      disabled={isSubmitting}
                                      className="button is-inverted w-button"
                                    />
                                  ) : (
                                    <Button
                                      type="submit"
                                      data={button}
                                      disabled={isSubmitting}
                                      className="button is-inverted w-button"
                                    />
                                  )}
                                  {/* <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="button is-inverted w-button"
                                  >
                                    {isSubmitting
                                      ? form?.submittingText
                                      : form?.submitText}
                                  </button> */}
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>

                        {/* <div
                          className="success-message brochure-download w-form-done"
                          tabIndex="-1"
                          role="region"
                          aria-label="Download Brochure Form success"
                        >
                          <div className="success-message-wrap">
                            <div className="rounded-icon is-success">
                              <Image
                              width={24}
                              height={24}
                                src="/images/home/64217eda6726034a4ce379d4_dark-green-check.svg"
                                loading="lazy"
                                alt=""
                              />
                            </div>
                            <div>
                              <div className="margin-bottom margin-tiny">
                                <h5>Successfully sent</h5>
                              </div>
                              <p>
                                If you have any questions, don&amp;t hesitate to{" "}
                                <a
                                  href="/contact"
                                  className="text-style-link is-blue"
                                >
                                  contact us
                                </a>
                                .
                              </p>
                            </div>
                          </div>
                        </div>
                        <div
                          className="error-message w-form-fail"
                          tabIndex="-1"
                          role="region"
                          aria-label="Download Brochure Form failure"
                        >
                          <div className="error-text">
                            Oops! Something went wrong while submitting the
                            form?.
                          </div>
                        </div> */}
                      </div>
                    </div>
                    <div
                      id="w-node-_289feebd-30be-03c0-d179-8bdc59f1a12c-59f1a10d"
                      className="cta-brochure-img"
                    >
                      <ResponsiveImage
                        width={640}
                        height={560}
                        src={image?.src}
                        lazyLoading
                        sizes="(min-width: 992px) 640px, 100vw"
                        alt={image?.alt}
                        className="cover-image"
                      />
                      <div className="brochure-specifications-wrap right-bottom">
                        <div className="rounded-tag">
                          <div>{file?.type?.toUpperCase()}</div>
                        </div>
                        <div className="rounded-tag">
                          <div>{file?.size}</div>
                        </div>
                      </div>
                    </div>
                    <a
                      data-w-id="289feebd-30be-03c0-d179-8bdc59f1a12e"
                      href="#"
                      className="close-btn-link position-abs-right-top-margins w-inline-block"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsShown(false);
                      }}
                    >
                      <Image
                        width={24}
                        height={24}
                        src="/images/home/63db8bbb24c0a691e0cc2cc8_close-icon.svg"
                        loading="lazy"
                        alt=""
                        className="icon-1x1-xsmall"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div
        data-w-id="289feebd-30be-03c0-d179-8bdc59f1a130"
        className="download-brochure-pop-up-bg"
        style={{ opacity: 1 }}
        onClick={() => setIsShown(false)}
      />
    </div>
  );
};

Brochure.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    title: PropTypes.node,
    titleMobile: PropTypes.node,
    text: PropTypes.node,
    textMobile: PropTypes.node,
    file: PropTypes.shape({
      type: PropTypes.string,
      size: PropTypes.string,
    }),
    image: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }),
    form: PropTypes.shape({
      firstnamePlaceholder: PropTypes.string,
      lastnamePlaceholder: PropTypes.string,
      phoneNumberPlaceholder: PropTypes.string,
      emailPlaceholder: PropTypes.string.isRequired, // Make email placeholder required
      privacyPolicyText: PropTypes.node, // Allows for various privacy policy content (e.g., strings or JSX)
      button: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonMobile: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSubmiting: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      buttonSubmitingMobile: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
        url: PropTypes.string,
        bgColor: PropTypes.string,
        bgColorHover: PropTypes.string,
        borderColor: PropTypes.string,
        borderColorHover: PropTypes.string,
        hide: PropTypes.bool,
      }),
      brochureLink: PropTypes.string,
    }),
  }),
};

const BrochureModal = ({ className = "", data }) => {
  const ref = useRef(null);
  useEffect(() => {
    const selector = "brochure-modal-wrapper";
    let brochureWrapper = document.getElementById(selector);
    if (!brochureWrapper) {
      brochureWrapper = document.createElement("div");
      brochureWrapper.id = selector;
      document.body.appendChild(brochureWrapper);
    }
    ref.current = brochureWrapper;
  }, []);

  return ref.current
    ? createPortal(<Brochure className={className} data={data} />, ref.current)
    : null;
};

BrochureModal.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({}),
};

export default BrochureModal;
